// Phase A.3 — Left goals sidebar rail.

import { Icon } from "../../components/icons/Icon.tsx";
import type { SikaGoalRow } from "../../sika/useSikaGoals.ts";

interface Props {
  goals: SikaGoalRow[];
  activeGoalId: string | null;
  onSelectGoal: (id: string | null) => void;
  onNewConversation?: () => void;
  loading: boolean;
}

function statusClass(status: string): string {
  if (status === "achieved") return "achieved";
  if (status === "draft") return "draft";
  return "";
}

export function SikaGoalsRail({ goals, activeGoalId, onSelectGoal, onNewConversation, loading }: Props): JSX.Element {
  const activeGoals = goals.filter((g) => g.status === "active" || g.status === "draft");
  const completedGoals = goals.filter(
    (g) => g.status === "achieved" || g.status === "cancelled" || g.status === "paused",
  );

  return (
    <div className="goals-rail">
      <div className="gr-head">
        <div className="ttl">Goals</div>
        <button type="button" className="new-goal" onClick={() => { onNewConversation?.(); onSelectGoal(null); }}>
          <Icon name="plus" size={14} />
          New conversation
        </button>
      </div>
      <div className="gr-body">
        {loading ? (
          <div className="gr-section">Loading…</div>
        ) : (
          <>
            {activeGoals.length > 0 && (
              <>
                <div className="gr-section">Active</div>
                {activeGoals.map((g) => (
                  <GoalTile
                    key={g.id}
                    goal={g}
                    active={activeGoalId === g.id}
                    onClick={() => onSelectGoal(g.id)}
                  />
                ))}
              </>
            )}
            {completedGoals.length > 0 && (
              <>
                <div className="gr-section">Completed</div>
                {completedGoals.map((g) => (
                  <GoalTile
                    key={g.id}
                    goal={g}
                    active={activeGoalId === g.id}
                    onClick={() => onSelectGoal(g.id)}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function GoalTile({
  goal,
  active,
  onClick,
}: {
  goal: SikaGoalRow;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  const target = Number(goal.target_amount);
  const current = Number(goal.current_amount);
  const pct = target > 0 ? Math.min((current / target) * 100, 100) : 0;

  return (
    <button
      type="button"
      className={`goal-tile ${statusClass(goal.status)} ${active ? "active" : ""}`}
      aria-current={active ? "true" : undefined}
      onClick={onClick}
    >
      <div className="gt-row1">
        <span className="gt-status" />
        <span className="gt-name">{goal.title}</span>
        <span className="gt-amt">
          GH₵ {target.toLocaleString()}
        </span>
      </div>
      {target > 0 && (
        <div className="gt-bar">
          <div className="fill" style={{ width: `${String(pct)}%` }} />
        </div>
      )}
      {goal.timeline_weeks != null && (
        <div className="gt-meta">{goal.timeline_weeks}w timeline</div>
      )}
    </button>
  );
}
